import moment from "moment";
import React from "react";
import { FormattedMessage } from "react-intl";
import styled from "styled-components";

import cuideoTheme from "../../../../../../containers/themes/defaultTheme";
import { CandidateStatusTag } from "../../../../features/candidate";

const StyledCard = styled.div<{ hovered?: boolean }>`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 1.5rem;
  border: 2px solid
    ${({ hovered }) => (hovered ? cuideoTheme.palette.primary.main : "#e7e7e7")};
  border-radius: 0.5rem;
  color: ${cuideoTheme.palette.primary.main};
  font-size: 0.95rem;
  cursor: pointer;
  background-color: ${({ hovered }) => (hovered ? "#fbfbfb" : "transparent")};
  transition: border-color 0.15s ease, background-color 0.15s ease;

  & + & {
    margin-top: 0.5rem;
  }
`;

const StyledInfo = styled.div`
  min-width: 0;
`;

const StyledSexo = styled.div`
  font-size: 0.8rem;
  color: #7d7d7d;
`;

const StyledLastActivity = styled.div`
  font-size: 0.8rem;
  color: #7d7d7d;
`;

const StyledMeta = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
`;

const StyledDistance = styled.span`
  font-size: 0.8rem;
  color: #7d7d7d;
  white-space: nowrap;
`;

interface IPrefilterCandidateCardProps {
  id: string;
  nombre?: string;
  apellidos?: string;
  apellidos2?: string;
  sexo?: string;
  estadoCuideo?: string;
  lastActivityTime?: string;
  distanceKm?: number;
  isHovered?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

/**
 * Tarjeta específica del listado de prefiltro de un contrato.
 * Muestra el nombre completo del candidato (nombre + apellidos + apellidos2),
 * su sexo, su estado Cuideo, la última actividad y la distancia en km al
 * contrato dentro de un recuadro, aislada aquí para poder personalizarla solo
 * en esta sección. Se resalta al pasar el hover, sincronizado con el marcador
 * del candidato en el mapa.
 */
const PrefilterCandidateCard: React.FC<IPrefilterCandidateCardProps> = ({
  id,
  nombre,
  apellidos,
  apellidos2,
  sexo,
  estadoCuideo,
  lastActivityTime,
  distanceKm,
  isHovered,
  onMouseEnter,
  onMouseLeave,
}) => {
  const fullName = [nombre, apellidos, apellidos2].filter(Boolean).join(" ");

  return (
    <StyledCard
      hovered={isHovered}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <StyledInfo>
        <div>{fullName}</div>
        {!!sexo && <StyledSexo>{sexo}</StyledSexo>}
        {!!lastActivityTime && (
          <StyledLastActivity>
            <FormattedMessage
              id="PrefilterCandidateCard.LastActivity"
              defaultMessage="Última actividad {hace}"
              values={{ hace: moment(lastActivityTime).fromNow() }}
            />
          </StyledLastActivity>
        )}
      </StyledInfo>
      <StyledMeta>
        {!!estadoCuideo && (
          <CandidateStatusTag candidateId={id} status={estadoCuideo} />
        )}
        {typeof distanceKm === "number" && (
          <StyledDistance>{`${Math.round(distanceKm)} km`}</StyledDistance>
        )}
      </StyledMeta>
    </StyledCard>
  );
};

export default PrefilterCandidateCard;
